import styled from '@emotion/styled'
import { FadeInOutAnimation } from 'app/components/Common/FadeInOutAnimation'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { Link } from 'gatsby'
import React, { memo } from 'react'
import * as Icon from 'react-feather'
import LazyLoad from 'react-lazyload'

export interface Props {
  buttonLabel?: string
  buttonURL: string
  image?: string
  sort?: number
  title?: string
}

export const Item = memo(function Item({
  buttonLabel,
  buttonURL,
  image,
  title,
}: Props) {
  if (!buttonURL) {
    return null
  }

  return (
    <Container to={buttonURL}>
      <FadeInOutAnimation>
        <Wrapper>
          {image ? (
            <LazyLoad>
              <BackgroundImage
                className="banner-cta-image"
                style={{ backgroundImage: `url(${image})` }}
              />
            </LazyLoad>
          ) : null}
          <Wrap dial={5} row space="between">
            {title ? <Title>{title}</Title> : null}
            {buttonLabel ? (
              <ButtonLabel dial={5} row>
                {buttonLabel}
                <Icon.ArrowRight />
              </ButtonLabel>
            ) : null}
          </Wrap>
        </Wrapper>
      </FadeInOutAnimation>
    </Container>
  )
})

const Container = styled(Link)`
  width: calc(50% - 1px);
  &:hover {
    .banner-cta-image {
      opacity: 1;
    }
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`

const Wrapper = styled.div`
  display: flex;
  position: relative;
`

const BackgroundImage = styled.span`
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  opacity: 0.2;
  position: absolute;
  top: 0;
  left: 0;
  transition: 0.3s ease-out;
`

const Wrap = styled(FlexBox)`
  width: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  margin: 2.5rem;
  padding: 3.8125rem 7.986vw;
  position: relative;
  z-index: 2;

  @media (max-width: 1199px) {
    margin: 1.5rem;
    padding: 3rem 2.25rem;
  }

  @media (max-width: 1023px) {
    padding: 1.5rem;
  }
`

const Title = styled.h4`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.875rem;
  line-height: 2.5rem;
  &:after {
    content: '';
    width: 3.75rem;
    height: 2px;
    background: ${({ theme }) => theme.colors.variants.neutralLight2};
    margin-left: 0.75rem;
  }

  @media (max-width: 374px) {
    &:after {
      display: none;
    }
  }
`

const ButtonLabel = styled(FlexBox)`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  line-height: 1rem;
  text-transform: uppercase;
  svg {
    height: 20px;
    stroke: ${({ theme }) => theme.colors.variants.primaryDark1};
    margin-left: 0.5rem;
  }
`
